import { useState, memo } from "react";
import { ThemedPrimaryButton } from "../components/ui/button";
import { ApiService } from "../services/AutoOnboardingApiService";
import { MIN_PASSWORD_LENGTH } from "../constants/user";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { lockIcon } from "../assets/images";
import { PasswordInput } from "../components/ui/input";
import { useToast } from "../components/ui/toast";
import { MIXPANEL_EVENTS } from "../constants/MixPanel";
import { trackEvent } from "../services/MixpanelService";
import { LightSurface } from "../common/LightSurface";

// Extracted password dialog component
const PasswordChangeDialog = memo(
  ({ showDialog, setShowDialog, onClose, onPasswordChange }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [formData, setFormData] = useState({
      password: "",
      confirmPassword: "",
    });
    const [errorPassword, setErrorPassword] = useState(false);
    const [errorConfirmPassword, setErrorConfirmPassword] = useState(false);
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);

    if (!showDialog) return null;

    const onSubmit = async (e) => {
      e.preventDefault();
      try {
        setLoading(true);
        setErrorPassword(null);
        setErrorConfirmPassword(null);
        const password = formData.password;
        const confirmPassword = formData.confirmPassword;

        if (password.length < MIN_PASSWORD_LENGTH) {
          setErrorPassword(
            `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`
          );
          return;
        }

        if (password !== confirmPassword) {
          setErrorConfirmPassword("Las contraseñas no coinciden");
          return;
        }

        const email = JSON.parse(localStorage.getItem("user")).email;
        const oldPassword = email;
        const newPassword = password;
        const data = await ApiService.changePassword(oldPassword, newPassword);

        if (data) {
          trackEvent(MIXPANEL_EVENTS.CHANGE_PASSWORD_SUCCESS);
          onClose();
          onPasswordChange();
        } else {
          setErrorPassword(
            "Error al cambiar la contraseña. Intente nuevamente."
          );
          trackEvent(MIXPANEL_EVENTS.CHANGE_PASSWORD_ERROR, {
            error: data.message || JSON.stringify(data),
          });
        }
        showToast("Contraseña creada correctamente", "success");
      } catch (error) {
        setErrorPassword("Error al cambiar la contraseña. Intente nuevamente.");
      } finally {
        setLoading(false);
      }
    };

    return (
      <LightSurface>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent
            showCloseButton={false}
            style={{ colorScheme: "light", color: "#0F1110" }}
          >
            <DialogHeader className="flex flex-col items-center justify-between gap-4">
              <div className="flex flex-col items-center justify-center gap-2">
                <img src={lockIcon} alt="lock" className="w-24" />
              </div>
              <div className="flex flex-col items-center justify-center gap-6">
                <form onSubmit={onSubmit}>
                  <DialogTitle className="text-3xl font-bold text-center">
                    Crea una nueva contraseña
                  </DialogTitle>
                  <DialogDescription className="pt-4 space-y-4 text-lg font-medium text-center">
                    <span className="text-neutralDarkBlack font-medium text-lg block">
                      Por seguridad, es necesario que definas una nueva
                      contraseña antes de continuar.
                    </span>
                    <div className="space-y-4 text-left">
                      <PasswordInput
                        label="Nueva contraseña"
                        showPassword={showPassword}
                        className="text-sm font-medium"
                        setShowPassword={setShowPassword}
                        placeholder="Ingresa tu nueva contraseña"
                        value={formData.password}
                        error={{ message: errorPassword }}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                      />

                      <PasswordInput
                        label="Confirmar nueva contraseña"
                        showPassword={showPasswordConfirm}
                        className="text-sm font-medium"
                        setShowPassword={setShowPasswordConfirm}
                        placeholder="Repite tu nueva contraseña"
                        value={formData.confirmPassword}
                        error={{ message: errorConfirmPassword }}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            confirmPassword: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-6 pt-4">
                      <ThemedPrimaryButton
                        type="submit"
                        disabled={loading}
                        className="text-base"
                      >
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <div className="h-5 w-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                          </div>
                        ) : (
                          "Guardar contraseña"
                        )}
                      </ThemedPrimaryButton>
                    </div>
                  </DialogDescription>
                </form>
              </div>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </LightSurface>
    );
  }
);
PasswordChangeDialog.displayName = "PasswordChangeDialog";

export default PasswordChangeDialog;
